import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../i18n";

const EASE = [0.22, 1, 0.36, 1];

const ITEMS = [
  { src: "/images/bisagra.jpg", cat: "repairs", es: "Pequeñas reparaciones", en: "Small home repairs", ar: "aspect-[4/3]" },
  { src: "/images/muebles.jpg", cat: "repairs", es: "Montaje de muebles", en: "Furniture assembly", ar: "aspect-[4/5]" },
  { src: "/images/tv.jpg", cat: "installations", es: "Televisión en la pared", en: "Wall-mounted TV", ar: "aspect-[4/3]" },
  { src: "/images/grifo.jpg", cat: "repairs", es: "Pequeña fontanería", en: "Small plumbing", ar: "aspect-[4/5]" },
  { src: "/images/cuadros.jpg", cat: "installations", es: "Cuadros, espejos y estanterías", en: "Pictures, mirrors & shelves", ar: "aspect-[4/3]" },
  { src: "/images/puerta.jpg", cat: "repairs", es: "Puertas, persianas y ajustes", en: "Doors, blinds & fixes", ar: "aspect-[4/3]" },
  { src: "/images/limpieza-alfombra.jpg", cat: "cleaning", es: "Limpieza de alfombras", en: "Carpet cleaning", ar: "aspect-square" },
  { src: "/images/limpieza-coche.jpg", cat: "cleaning", es: "Limpieza de asientos de coche", en: "Car seat cleaning", ar: "aspect-[4/5]" },
];

const KEYS = ["all", "repairs", "installations", "cleaning"];
const FILTER_IDS = ["all", "repairs", "installations", "cleaning"];

const Gallery = () => {
  const { t, lang } = useLanguage();
  const [filter, setFilter] = useState("all");
  const visible = ITEMS.filter((it) => filter === "all" || it.cat === filter);

  return (
    <section id="galeria" className="py-24 sm:py-32" data-testid="work-gallery-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, ease: EASE }}
              className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#C85A32]"
            >
              {t.gallery.eyebrow}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.08, ease: EASE }}
              className="max-w-xl font-serif text-4xl font-black tracking-tight text-[#0D1B2A] sm:text-5xl"
            >
              {t.gallery.title}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.16, ease: EASE }}
              className="mt-4 max-w-md text-base text-[#0D1B2A]/60"
            >
              {t.gallery.sub}
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2, ease: EASE }}
            className="flex flex-wrap gap-2"
          >
            {KEYS.map((k, i) => (
              <button
                key={k}
                data-testid={`gallery-filter-${FILTER_IDS[i]}`}
                onClick={() => setFilter(k)}
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                  filter === k
                    ? "border-[#0D1B2A] bg-[#0D1B2A] text-[#FAF6F0]"
                    : "border-[#0D1B2A]/15 text-[#0D1B2A]/60 hover:border-[#C85A32] hover:text-[#C85A32]"
                }`}
              >
                {t.gallery.filters[i]}
              </button>
            ))}
          </motion.div>
        </div>

        <motion.div layout className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {visible.map((item, i) => (
              <motion.figure
                layout
                key={item.src}
                data-testid={`gallery-card-${i + 1}`}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.45, ease: EASE }}
                className={`group relative overflow-hidden rounded-3xl ${item.ar}`}
              >
                <img
                  src={item.src}
                  alt={lang === "es" ? item.es : item.en}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                />
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default Gallery;
